
/**
 * 日曆事件即時通知 Hook
 * 當有新的日曆事件建立時，播放提示音通知管理員
 */

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

// 提示音 - 使用 Web Audio API 產生「叮叮」聲
function playNotificationSound() {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // 第一聲「叮」
    const playTone = (startTime: number, frequency: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      // 音量漸變效果
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);

      oscillator.start(startTime);
      oscillator.stop(startTime + 0.3);
    };

    const now = audioContext.currentTime;

    // 播放兩聲「叮叮」
    playTone(now, 880); // A5 音符
    playTone(now + 0.15, 1047); // C6 音符（更高一點）

    // 清理 AudioContext
    setTimeout(() => {
      audioContext.close().catch((e) => console.warn('AudioContext close failed', e));
    }, 1000);
  } catch (error) {
    console.warn('無法播放提示音:', error);
  }
}

interface CalendarEvent {
  id: string;
  order_id: string;
  location_id: string;
  event_title: string;
  created_at: string;
}

interface UseCalendarNotificationOptions {
  enabled?: boolean;
  onNewEvent?: (event: CalendarEvent) => void;
}

export function useCalendarNotification(options: UseCalendarNotificationOptions = {}) {
  const { enabled = true, onNewEvent } = options;
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastEventIdRef = useRef<string | null>(null);

  const handleNewEvent = useCallback(
    (payload: any) => {
      const newEvent = payload.new as CalendarEvent;

      // 避免重複通知
      if (lastEventIdRef.current === newEvent.id) {
        return;
      }
      lastEventIdRef.current = newEvent.id;

      console.log('🔔 收到新日曆事件:', newEvent.event_title);

      // 播放提示音
      playNotificationSound();

      // 呼叫回調函數
      if (onNewEvent) {
        onNewEvent(newEvent);
      }
    },
    [onNewEvent]
  );

  useEffect(() => {
    if (!enabled) return;

    // 訂閱 order_calendar_events 表的 INSERT 事件
    const channel = supabase
      .channel('calendar-events-notification')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_calendar_events',
        },
        handleNewEvent
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ 已訂閱日曆事件通知');
        }
      });

    channelRef.current = channel;

    // 清理訂閱
    return () => {
      if (channelRef.current) {
        console.log('🔌 取消訂閱日曆事件通知');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, handleNewEvent]);

  return {
    // 手動播放提示音（用於測試）
    playSound: playNotificationSound,
  };
}

export default useCalendarNotification;
